import { useEffect, useRef, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { navigateToCategory } from "../../utils/navigation";
import {
  Menu,
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
} from "lucide-react";
import type { CategoriesProps } from "../../types/Categories";

interface NestedSubCategoryProps {
  subCategory: any;
  parentCategoryId: number;
  level?: number;
  onClose: () => void;
  depth?: number;
}

const NestedSubCategory: FC<NestedSubCategoryProps> = ({
  subCategory,
  parentCategoryId,
  level = 0,
  onClose,
  depth = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasChildren =
    subCategory.sub_categories && subCategory.sub_categories.length > 0;

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    if (hasChildren && !isExpanded) {
      hoverTimeoutRef.current = setTimeout(() => setIsExpanded(true), 200);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as Node;
    if (containerRef.current?.contains(relatedTarget)) return;
    setIsHovering(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hasChildren && isExpanded) {
      leaveTimeoutRef.current = setTimeout(() => setIsExpanded(false), 300);
    }
  };

  useEffect(() => () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
  }, []);

  void parentCategoryId;
  void depth;

  return (
    <div
      ref={containerRef}
      className={level > 0 ? "ml-3" : ""}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`flex items-center justify-between p-2.5 mb-1 transition-all duration-200 cursor-pointer
          ${isHovering ? "bg-header-surface shadow-sm" : "hover:bg-header-surface"}
          ${level === 0 ? "border border-header-border" : "border border-transparent hover:border-header-border"}
        `}
      >
        <Link
          to={navigateToCategory(subCategory.id)}
          className="flex-1 flex items-center space-x-2.5"
          onClick={onClose}
        >
          <div
            className={`shrink-0 ${level === 0 ? "w-9 h-9" : level === 1 ? "w-7 h-7" : "w-6 h-6"}
              bg-header-surface border border-header-border flex items-center justify-center transition-all duration-200
              ${isHovering ? "border-primary" : ""}
            `}
          >
            {level === 0 ? (
              <Package className="w-4 h-4 text-primary" />
            ) : (
              <span className={`font-bold ${level === 0 ? "text-xs" : "text-[10px]"} text-primary`}>
                {subCategory.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h5
                className={`font-medium ${level === 0 ? "text-[13px]" : level === 1 ? "text-xs" : "text-[11px]"}
                  ${isHovering ? "text-accent" : "text-primary"} transition-colors truncate`}
              >
                {subCategory.name}
              </h5>
              {hasChildren && (
                <span className="text-[9px] bg-header-hover text-header-muted px-1.5 py-0.5 font-medium">
                  {subCategory.sub_categories.length}
                </span>
              )}
            </div>
            {subCategory.description && level === 0 && (
              <p className="text-[11px] text-header-muted mt-0.5 line-clamp-1">
                {subCategory.description}
              </p>
            )}
          </div>
        </Link>
        {hasChildren && (
          <ChevronRight
            className={`w-4 h-4 transition-all duration-300 ${isHovering ? "text-accent" : "text-header-muted"} ${isExpanded ? "rotate-90" : ""}`}
          />
        )}
      </div>

      {hasChildren && isExpanded && (
        <div
          className={`ml-2 border-l-2 ${isHovering ? "border-accent/50" : "border-header-border"} pl-3 mt-1 space-y-0.5`}
        >
          {subCategory.sub_categories.map((nestedSub: any) => (
            <NestedSubCategory
              key={nestedSub.id}
              subCategory={nestedSub}
              parentCategoryId={subCategory.id}
              level={level + 1}
              depth={0}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Categories: FC<CategoriesProps> = ({
  categoriesLoading,
  categoriesError,
  categoriesWithSubs,
  categoryHoverDropdown,
}) => {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isCategoriesVisible, setIsCategoriesVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const displayCategories = categoriesWithSubs || [];

  useEffect(() => {
    if (displayCategories.length > 0 && !activeCategory) {
      setActiveCategory(displayCategories[0].id.toString());
    }
  }, [displayCategories, activeCategory]);

  const handleCategoriesMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsCategoriesOpen(true);
    setIsCategoriesVisible(true);
  };

  const handleCategoriesMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsCategoriesOpen(false);
      setTimeout(() => setIsCategoriesVisible(false), 100);
    }, 100);
  };

  return (
    <div className="bg-white border-b border-header-border relative">
      <div className="max-w-7xl mx-auto px-10">
        <div className="flex items-center">

          {/* All Perfumes button */}
          <div
            className="shrink-0"
            onMouseEnter={categoryHoverDropdown ? handleCategoriesMouseEnter : undefined}
            onMouseLeave={categoryHoverDropdown ? handleCategoriesMouseLeave : undefined}
          >
            <Link
              to="/products"
              className="flex items-center gap-2 px-[18px] py-[11px] bg-primary text-primary-foreground hover:bg-primary-light transition-colors"
              style={{ borderRadius: 0 }}
            >
              <Menu className="w-[15px] h-[15px]" />
              <span className="text-[12px] font-medium tracking-[0.4px]">All Perfumes</span>
              {categoryHoverDropdown && (
                <ChevronDown
                  className={`w-[10px] h-[10px] transition-transform duration-200 ${isCategoriesOpen ? "rotate-180" : ""}`}
                />
              )}
            </Link>

            {/* All-categories mega dropdown */}
            {categoryHoverDropdown && isCategoriesVisible && (
              <div
                className={`absolute top-full left-0 right-0 bg-white border border-header-border shadow-2xl z-50 transition-opacity duration-200 ${
                  isCategoriesOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onMouseEnter={handleCategoriesMouseEnter}
                onMouseLeave={handleCategoriesMouseLeave}
              >
                {categoriesLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 animate-spin text-accent" />
                    <span className="ml-3 text-header-muted text-[13px]">Loading categories...</span>
                  </div>
                ) : categoriesError ? (
                  <div className="flex items-center justify-center p-12 text-red-500">
                    <span className="text-[13px] font-medium">Failed to load categories</span>
                  </div>
                ) : (
                  <div className="flex max-w-350 mx-auto min-h-[280px]">
                    {/* Left: category list */}
                    <div className="w-[220px] shrink-0 border-r border-header-border bg-header-surface">
                      <div className="py-2">
                        {displayCategories.map((category) => (
                          <div
                            key={category.id}
                            className={`flex items-center justify-between px-5 py-[11px] cursor-pointer transition-all duration-150
                              border-l-2 ${activeCategory === category.id.toString()
                                ? "border-accent bg-white text-primary"
                                : "border-transparent text-primary hover:bg-white/60"}
                            `}
                            onMouseEnter={() => setActiveCategory(category.id.toString())}
                          >
                            <Link
                              to={navigateToCategory(category.id)}
                              className="flex-1 flex items-center gap-2.5"
                              onClick={() => { setIsCategoriesOpen(false); setActiveCategory(null); }}
                            >
                              {category.icon_path && (
                                <div className="shrink-0 w-7 h-7 border border-header-border flex items-center justify-center p-1">
                                  <img
                                    src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/publicimages/category_images/${category.icon_path}`}
                                    alt={category.name}
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              )}
                              <span className="text-[13px] font-medium">{category.name}</span>
                            </Link>
                            {category.sub_categories?.length ? (
                              <ChevronRight
                                className={`w-[13px] h-[13px] ${activeCategory === category.id.toString() ? "text-accent" : "text-header-muted"}`}
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right: subcategories */}
                    <div className="flex-1 bg-white">
                      {activeCategory ? (() => {
                        const selectedCategory = displayCategories.find(
                          (cat) => cat.id.toString() === activeCategory
                        );
                        return (
                          <>
                            <div className="px-8 py-4 border-b border-header-border flex items-center justify-between">
                              <div
                                className="text-[22px] font-normal text-primary"
                                style={{ fontFamily: "var(--font-display)" }}
                              >
                                {selectedCategory?.name}
                              </div>
                              <Link
                                to={navigateToCategory(selectedCategory?.id || 0)}
                                className="px-[18px] py-[7px] border border-primary text-primary text-[11px] font-medium tracking-[1px] uppercase hover:bg-primary hover:text-white transition-colors"
                                style={{ borderRadius: 0 }}
                                onClick={() => { setIsCategoriesOpen(false); setActiveCategory(null); }}
                              >
                                View All
                              </Link>
                            </div>
                            <div className="max-h-[380px] overflow-y-auto p-6">
                              {selectedCategory?.sub_categories?.length ? (
                                <div className="grid grid-cols-4 gap-2.5">
                                  {selectedCategory.sub_categories.map((subcat: any) => (
                                    <div
                                      key={subcat.id}
                                      className="p-3 border border-header-border cursor-pointer hover:border-primary transition-colors"
                                    >
                                      <Link
                                        to={navigateToCategory(subcat.id)}
                                        className="block"
                                        onClick={() => { setIsCategoriesOpen(false); setActiveCategory(null); }}
                                      >
                                        <div className="h-12 bg-header-surface mb-2.5 flex items-center justify-center">
                                          <span className="text-[9px] text-header-muted tracking-[1px] uppercase">brand</span>
                                        </div>
                                        <div className="text-[12px] font-medium text-primary mb-0.5">{subcat.name}</div>
                                        {subcat.sub_categories?.length ? (
                                          <div className="text-[10px] text-header-muted">{subcat.sub_categories.length} sub</div>
                                        ) : null}
                                      </Link>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-8 text-header-muted">
                                  <Package className="w-10 h-10 mb-2 text-header-border" />
                                  <p className="text-[13px]">No subcategories</p>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })() : (
                        <div className="flex items-center justify-center h-full text-header-muted">
                          <p className="text-[13px]">Hover a category</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Popular tags footer */}
                <div className="border-t border-header-border px-8 py-2.5 bg-header-surface flex items-center gap-6">
                  <span className="text-[11px] text-header-muted">Popular:</span>
                  {["Oud Collection", "Best Sellers", "New Arrivals", "Gift Sets"].map((tag) => (
                    <Link
                      key={tag}
                      to="/products"
                      className="text-[11px] text-primary border-b border-header-border pb-px hover:border-primary hover:text-accent transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Separator */}
          <span className="w-px h-[14px] bg-header-border mx-5 shrink-0" />

          {/* Category tabs */}
          <nav className="flex items-center">
            {displayCategories.slice(0, 6).map((category) => (
              <div key={category.id} className="group relative">
                <Link
                  to={navigateToCategory(category.id)}
                  className="flex items-center gap-[5px] px-4 py-[11px] text-[12.5px] text-primary font-normal border-b-2 border-transparent hover:border-accent -mb-px transition-colors duration-150"
                >
                  <span>{category.name}</span>
                  {category.sub_categories?.length ? (
                    <ChevronDown className="w-[10px] h-[10px] text-header-muted transition-transform duration-200 group-hover:rotate-180" />
                  ) : null}
                </Link>

                {categoryHoverDropdown && category.sub_categories?.length ? (
                  <div
                    className="absolute left-0 top-full bg-white border border-header-border shadow-xl z-50
                      opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[240px]"
                  >
                    <div className="p-3 border-b border-header-border">
                      <h4 className="text-[13px] font-medium text-primary">{category.name}</h4>
                    </div>
                    <div className="max-h-[340px] overflow-y-auto p-3">
                      <div className="space-y-1">
                        {category.sub_categories.map((subcat: any) => (
                          <NestedSubCategory
                            key={subcat.id}
                            subCategory={subcat}
                            parentCategoryId={category.id}
                            onClose={() => {}}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Categories;
